import React, { useState } from "react";
import { Image, X, ExternalLink } from "lucide-react";
import { Label } from "@/components/atoms/Label";
import { IconBucketModal } from "./IconBucketModal";
import type { BaseFieldProps } from "../types";

interface IconBucketFieldProps extends BaseFieldProps {
  filterCategory?: "images" | "icons" | "all";
}

// Button state configurations
const BUTTON_STATE_STYLES = {
  error: "border-secondary-main bg-red-50 text-red-700",
  selected: "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100",
  default: "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400",
} as const;

const BUTTON_TEXT_CONFIG = {
  selected: "Change from Icon Bucket",
  default: "Choose from Icon Bucket",
} as const;

const IconBucketField: React.FC<IconBucketFieldProps> = ({
  fieldName,
  label,
  placeholder,
  disabled,
  className,
  required,
  hasError,
  validationMessage,
  formData,
  handleChange,
  filterCategory = "all",
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentValue = formData[fieldName] || "";

  const handleSelect = (url: string) => {
    handleChange(fieldName, url);
  };

  const handleClear = () => {
    handleChange(fieldName, "");
  };

  // Helper functions for styling
  const getButtonStateStyle = () => {
    if (hasError) return BUTTON_STATE_STYLES.error;
    if (currentValue) return BUTTON_STATE_STYLES.selected;
    return BUTTON_STATE_STYLES.default;
  };

  const getButtonText = () => {
    if (currentValue) return BUTTON_TEXT_CONFIG.selected;
    return placeholder || BUTTON_TEXT_CONFIG.default;
  };

  const getButtonClassName = () => {
    const baseClasses = "w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg transition-colors";
    const stateStyle = getButtonStateStyle();
    const disabledClass = disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer";
    const customClass = className || "";
    
    return `${baseClasses} ${stateStyle} ${disabledClass} ${customClass}`;
  };

  return (
    <>
      <div className="flex-1 min-w-80 max-w-md mb-4">
        {label && (
          <Label
            htmlFor={fieldName}
            className="block text-body-3-semibold font-semibold text-black mb-2"
          >
            {label}
            {required && <span className="text-secondary-main ml-1">*</span>}
          </Label>
        )}

        {/* Selected Image Preview */}
        {currentValue && (
          <div className="mb-3 relative group">
            <div className="w-full h-32 bg-gray-50 border-2 border-gray-200 rounded-lg overflow-hidden flex items-center justify-center">
              <img
                src={currentValue}
                alt="Selected"
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150'%3E%3Crect fill='%23f3f4f6' width='200' height='150'/%3E%3Ctext fill='%239ca3af' x='50%25' y='50%25' text-anchor='middle' dy='.3em' font-family='sans-serif'%3EImage Not Found%3C/text%3E%3C/svg%3E";
                }}
              />
            </div>

            {/* Overlay with actions */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <a
                href={currentValue}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="w-4 h-4 text-gray-700" />
              </a>
              <button
                onClick={handleClear}
                disabled={disabled}
                className="p-2 bg-white rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                title="Clear selection"
              >
                <X className="w-4 h-4 text-red-600" />
              </button>
            </div>

            {/* URL Display */}
            <p className="text-xs text-gray-500 mt-2 truncate" title={currentValue}>
              {currentValue}
            </p>
          </div>
        )}

        {/* Select Button */}
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          disabled={disabled}
          className={getButtonClassName()}
        >
          <Image className="w-5 h-5" />
          <span className="font-medium">{getButtonText()}</span>
        </button>

        {hasError && (
          <p className="text-secondary-main text-body-4-medium mt-2">
            {validationMessage || "This field is required"}
          </p>
        )}
      </div>

      {/* Modal */}
      <IconBucketModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={handleSelect}
        currentValue={currentValue}
        filterCategory={filterCategory}
      />
    </>
  );
};

export default IconBucketField;
